import commaNumber from 'comma-number';

import {
  getMaxItemCountByScraper,
  getSourceByScraperNameSafe,
} from '@/features/Scraping/configs/config-actions';
import type { SCRAPER_NAME } from '@/features/Scraping/types';
import { LANGUAGE } from '@/types/commonConst';
import { getFeedbackSourceName } from '@/types/feedbackConst';
import { mapDynamicMessage } from '@/utils/string';

import type { MessageMap } from '..';

const getMaxItemGuideMD = (
  {
    collect_count,
    feedback_count,
    scraper_name,
  }: Record<string, string | number>,
  messages: {
    full_message: string;
    max_item_message: string;
  },
): string => {
  const scraper = scraper_name as SCRAPER_NAME;
  const duplicated = Number(collect_count) - Number(feedback_count);
  const max_count = getMaxItemCountByScraper(scraper);
  const source = getSourceByScraperNameSafe(scraper);
  const source_name = source ? getFeedbackSourceName(source) : '';

  const max_count_message = max_count && source_name
    ? mapDynamicMessage(messages.max_item_message, {
        source_name,
        max_count: commaNumber(max_count),
      })
    : '';

  return mapDynamicMessage(messages.full_message, {
    feedback_count: commaNumber(feedback_count),
    duplicated: commaNumber(duplicated),
    collect_count: commaNumber(collect_count),
    max_count_message: max_count_message,
  });
};

export const scrapingMessages = {
  PRODUCT_INFO_GUIDE_TITLE: {
    [LANGUAGE.EN]:
      'Please check existing brand, product line, product, and option names first.',
    [LANGUAGE.KO]:
      '기존에 생성된 브랜드명, 제품 라인, 제품명, 옵션명을 먼저 확인해 주세요.',
  },
  PRODUCT_INFO_GUIDE_DESC: {
    [LANGUAGE.EN]:
      'Entering the same product information differently may cause inaccurate data in feedback reports and charts.',
    [LANGUAGE.KO]:
      '동일한 제품 정보를 다르게 입력하면 피드백 집계 시 데이터가 정확하게 반영되지 않을 수 있습니다.',
  },
  NAVER_CAFE_SOURCE_GUIDE_TITLE: {
    [LANGUAGE.EN]: 'Test your keywords in Naver search first',
    [LANGUAGE.KO]: '네이버 검색에서 키워드를 먼저 테스트해 주세요',
  },
  NAVER_CAFE_SOURCE_GUIDE_DESC: {
    [LANGUAGE.EN]: `
Naver shows only the most recent 1,020 items per search. Proper keyword filtering helps achieve better date distribution.

How to test exclude keywords:
  1. Search your keywords on Naver.com
  2. Check if irrelevant results appear
  3. Test exclusions by adding ! before keywords (e.g., "baby food !advertisement")
`,
    [LANGUAGE.KO]: `
Naver는 검색 결과로 최근 1,020개의 항목만 표시합니다. 적절한 키워드 필터링은 더 나은 날짜 분포를 달성하는 데 도움이 됩니다.

제외 키워드 테스트 방법:
  1. Naver.com에서 키워드 검색
  2. 관련 없는 결과가 나타나는지 확인
  3. 키워드 앞에 !를 추가하여 제외 테스트 (예: "baby food !advertisement")
`,
  },
  MAX_ITEM_GUIDE_MD: {
    [LANGUAGE.EN]: ({
      collect_count,
      feedback_count,
      scraper_name,
    }: Record<string, string | number>): string => {
      return getMaxItemGuideMD(
        {
          collect_count,
          feedback_count,
          scraper_name,
        },
        {
          max_item_message:
            "- Due to {{source_name}}'s policy, only **up to {{max_count}} feedbacks** can be collected regardless of the actual number of feedbacks.",
          full_message: `
| Newly collected | Previously collected | **Total feedbacks** |
|----------------|-------------|-------------|
| {{feedback_count}} | {{duplicated}} | **{{collect_count}}** |
- Duplicate feedbacks from previous jobs are excluded. Only new feedbacks will be collected.
{{max_count_message}}
`,
        },
      );
    },
    [LANGUAGE.KO]: ({
      collect_count,
      feedback_count,
      scraper_name,
    }: Record<string, string | number>): string => {
      return getMaxItemGuideMD(
        {
          collect_count,
          feedback_count,
          scraper_name,
        },
        {
          max_item_message:
            '- {{source_name}} 정책에 따라, 실제 피드백 수와 관계없이 **최대 {{max_count}}개**까지만 수집됩니다.',
          full_message: `
| 신규 생성된 피드백 | 기존에 수집된 피드백 | **총 피드백** |
|----------------|-------------|-------------|
| {{feedback_count}} | {{duplicated}} | **{{collect_count}}** |
- 기존 요청에서 중복된 피드백이 존재할 경우, 중복 항목을 제외하고 일부 피드백만 신규 생성됩니다.
{{max_count_message}}
`,
        },
      );
    },
  },
} as const satisfies MessageMap;
